import { useEffect, useRef, useState } from "react";

// Renders a form field's typed value instantly from local state (no network
// round trip) while writing to the backend on a debounce. The previous
// design called the parent's onUpdate synchronously on every keystroke,
// which round-tripped through a Supabase write + query refetch before the
// controlled input's own value prop ever reflected what was typed —
// visible lag on every character. Callers should mount their EditPanel
// with `key={block.id}` (see BlockEditPanel) so switching the selected
// block resets this hook's local state instead of carrying over the
// previous block's draft.
export function useDebouncedBlockContent<T>(initialContent: T, onUpdate: (content: T) => void, delayMs = 500) {
  const [draft, setDraft] = useState<T>(initialContent);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const update = (next: T, options?: { immediate?: boolean }) => {
    setDraft(next);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (options?.immediate) {
      timeoutRef.current = null;
      onUpdate(next);
    } else {
      timeoutRef.current = setTimeout(() => onUpdate(next), delayMs);
    }
  };

  return [draft, update] as const;
}
